declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag: (...args: unknown[]) => void;
	}
}

export const gtag = (...args: unknown[]): void => {
	if (typeof window !== 'undefined') {
		if (typeof window.gtag === 'undefined') {
			window.dataLayer = window.dataLayer ?? [];
			// oxlint-disable-next-line consistent-function-scoping
			window.gtag = function gtag() {
				window.dataLayer?.push(arguments);
			};
		}

		window.gtag(...args);
	}
};

let gtagLoaderRequested = false;

/**
 * Injects gtag.js after hydration, never from `head()`: a childless `<script src>` there became a #418
 * `args[]=HTML` on five production builds (DR-24, docs/notes/portals/head-bootstrap.md).
 *
 * **Call it only once the donor has consented to analytics** (DR-23). This is the single gate: {@link gtag}
 * queues into `dataLayer` and the queue is dropped if the loader never arrives, so the `config` and
 * `page_view` calls stay unguarded.
 */
export const loadGtag = (measurementId: string): void => {
	if (typeof document === 'undefined' || !measurementId || gtagLoaderRequested) return;
	gtagLoaderRequested = true;
	const script = document.createElement('script');
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
	script.async = true;
	document.head.appendChild(script);
};

/** PII or credentials, including a `redirect` target that embeds them. */
const SENSITIVE_ANALYTICS_PARAMS = new Set(['email', 'redirect', 'session', 'token', 'access_token', 'password']);

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/** Never reported; the reserved `.invalid` TLD makes a leak obvious rather than plausible. */
const RELATIVE_HREF_BASE = 'https://relative.invalid';

/** TanStack's history reports an in-app navigation as a relative href. */
const parseHref = (href: string): { url: URL; wasRelative: boolean } | undefined => {
	try {
		return { url: new URL(href), wasRelative: false };
	} catch {
		try {
			return { url: new URL(href, RELATIVE_HREF_BASE), wasRelative: true };
		} catch {
			return undefined;
		}
	}
};

/**
 * Emails and password-reset keys travel in the query string (`/confirm/code?email=…`), so they are
 * redacted by name and by an email-shaped catch-all that also covers a nested `redirect`.
 *
 * The benign params stay: the visitor's filters (`?year=`, `?causes=`, `?q=`…) are what make a portal's
 * page views readable in GA4. A relative href yields the same string as a direct hit (TEC-3823).
 */
export const sanitizeAnalyticsUrl = (href: string): string => {
	const parsed = parseHref(href);
	if (!parsed) {
		return href.split('?')[0] ?? href;
	}
	const { url, wasRelative } = parsed;
	const keysToRedact: string[] = [];
	url.searchParams.forEach((value, key) => {
		if (SENSITIVE_ANALYTICS_PARAMS.has(key.toLowerCase()) || EMAIL_PATTERN.test(value)) {
			keysToRedact.push(key);
		}
	});
	for (const key of keysToRedact) {
		url.searchParams.set(key, 'redacted');
	}
	return wasRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString();
};
